/**
 * One file, large, with what was measured about it.
 *
 * The product could already open a picture: a bare `<a target="_blank">` to
 * the preview endpoint, which left the customer on a browser tab containing a
 * JPEG and nothing else - no filename, no verdict, no way back, and no way to
 * see the next photograph. Deciding "which of these forty do I re-take?" meant
 * opening and closing forty tabs.
 *
 * Three things it is careful about:
 *
 * **The picture is a derivative, and it says so.** The bytes are a bounded
 * JPEG, never the original. `PreviewImage` owns that surface - the
 * magnification, its ceiling, and the caption naming the resolution on screen
 * against the one that was uploaded - because judging sharpness turned out to
 * be a screen of its own rather than an `<img>`.
 *
 * **A video shows its poster frame.** There is no player here and none is
 * implied: the frame is labelled as a frame.
 *
 * **The verdict is the stored one.** The same words the cards and the analysis
 * centre use, from `mediaAnalysis`, including the two that are not verdicts -
 * "Analiz edilmedi" and "Analiz durumu okunamadı".
 *
 * **The counter counts the session, not the page.** `items` is whatever has
 * been loaded so far - one hundred of a hundred and fifty - and the dialog
 * used to render `items.length` as the denominator, so a customer with a
 * hundred and fifty photographs opened the first one and read "1 / 100". The
 * denominator is `total` now, and walking past the loaded edge asks for the
 * next page rather than stopping at it. This is the same mistake the grid
 * behind it made and `MediaCountTruth` was written to fix; it survived here
 * because the dialog had no idea a total existed.
 */

import { useEffect, useRef } from "react";

import type { MediaAsset, SubjectType } from "@/api/types";
import { Button, StatusBadge } from "@/components/ui";
import { MEDIA_TYPE_LABELS, SEVERITY_LABELS, SEVERITY_TONES } from "@/i18n/terms";
import { formatBytes, formatDateTime, formatDimensions, formatDuration } from "@/lib/format";
import { guidedRegionLabel } from "@/features/coverage/guidedCoverage";
import { DeleteMediaButton } from "./DeleteMediaButton";
import { PreviewImage } from "./PreviewImage";
import { issueGuidance, verdictOf, type MediaAnalysis } from "./mediaAnalysis";
import { useDialogFocus } from "@/lib/useDialogFocus";

export interface PreviewItem {
  asset: MediaAsset;
  analysis: MediaAnalysis;
}

export function MediaPreviewDialog({
  items,
  index,
  subjectType,
  total,
  loadingMore = false,
  onNeedMore,
  onIndex,
  onClose,
  onChanged,
}: {
  items: readonly PreviewItem[];
  index: number;
  /** Decides whether this project has capture angles at all. */
  subjectType: SubjectType;
  /**
   * How many files the session actually holds, counted server-side.
   *
   * Optional, and absent means "the loaded set is the whole set" - the
   * dialogs that open a hand-built list of two problem files have no page
   * behind them and no total to report. It is never derived from `items`.
   */
  //
  // `| undefined` explicitly, not just `?`. This project builds with
  // `exactOptionalPropertyTypes`, under which "may be omitted" and "may be
  // present and undefined" are different types - and the caller passes
  // `mediaSummary.data?.total`, which is the second.
  total?: number | undefined;
  /** A page is in flight, so "Sonraki" says so instead of looking dead. */
  loadingMore?: boolean | undefined;
  /** Fetch the next page. Called when walking off the loaded edge. */
  onNeedMore?: (() => void) | undefined;
  onIndex: (next: number) => void;
  onClose: () => void;
  /** Re-read the session from the server after a change made in here. */
  onChanged: () => void;
}) {
  const dialog = useRef<HTMLDivElement>(null);
  const current = items[index];
  // The denominator. `items.length` only when nobody told us better - never
  // as a fallback that quietly shrinks a real session to its first page.
  const count = total ?? items.length;
  const loadedEdge = index >= items.length - 1;
  const moreToLoad = items.length < count;
  // Enabled on the last loaded file when the session has more, because that
  // press is what fetches them. Disabled only at the true end of the set.
  const canGoNext = index < count - 1;

  // Set when a press walked off the loaded edge, cleared when the page it
  // asked for arrives. A ref rather than state because it must not itself
  // cause a render - the render comes from `items` growing.
  const awaitingPage = useRef(false);

  const goNext = () => {
    if (!canGoNext) return;
    if (loadedEdge) {
      // Off the edge of what is in memory. Ask for the page and stay put; the
      // index moves when the files arrive, in the effect below. Advancing
      // first would leave an empty frame on screen for as long as the request
      // took, which reads as the picture having failed.
      if (moreToLoad && !loadingMore) {
        awaitingPage.current = true;
        onNeedMore?.();
      }
      return;
    }
    onIndex(index + 1);
  };

  // The page that was waited for landed. Step onto the first file of it.
  //
  // Guarded on `awaitingPage`, so a page loaded by the grid behind this
  // dialog - "Daha fazla yükle" is still there - does not yank the reader
  // forward off the photograph they were looking at.
  const loadedCount = items.length;
  useEffect(() => {
    if (!awaitingPage.current || loadingMore) return;
    awaitingPage.current = false;
    if (index < loadedCount - 1) onIndex(index + 1);
  }, [loadedCount, loadingMore, index, onIndex]);

  // The key handler is subscribed once; this keeps it calling the current
  // `goNext` rather than the one from the render it was attached in.
  const goNextRef = useRef(goNext);
  goNextRef.current = goNext;

  // Focus and its return only. The key listener below stays its own effect:
  // it also drives the arrows and needs the current `index`.
  useDialogFocus(dialog);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      // The arrows people already press in every other picture viewer. Guarded
      // at the ends rather than wrapping: a silent jump from the last file
      // back to the first reads as a bug on a set nobody can see the end of.
      if (event.key === "ArrowRight") goNextRef.current();
      if (event.key === "ArrowLeft" && index > 0) onIndex(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onIndex, index]);

  // The set can shrink under this dialog - a delete in here is the usual way.
  // Closing is the honest answer; keeping a frame open on a file that no
  // longer exists is not.
  useEffect(() => {
    if (!current) onClose();
  }, [current, onClose]);
  if (!current) return null;

  const { asset, analysis } = current;
  const verdict = verdictOf(analysis);
  const issues = analysis.state === "assessed" ? (analysis.assessment.issues ?? []) : [];
  const dimensions = formatDimensions(asset.width, asset.height);

  return (
    <div className="pairing-backdrop" role="presentation" onClick={onClose}>
      <div
        className="pairing-dialog media-viewer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="media-viewer-title"
        tabIndex={-1}
        ref={dialog}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="media-viewer-title" className="media-viewer__title" title={asset.original_filename}>
          {asset.original_filename}
        </h2>

        <PreviewImage asset={asset} />

        <div className="media-viewer__row">
          <StatusBadge tone={verdict.tone}>
            <span aria-hidden="true">{verdict.glyph}</span> {verdict.label}
          </StatusBadge>
          {/*
            Which angle the file was marked for. Only where the project has
            angles: a head project's coverage is measured, not chosen, and
            showing an empty "Çekim açısı" on one would be inventing a field.
          */}
          {subjectType !== "human_head" && (
            <span className="faint">
              Çekim açısı:{" "}
              {guidedRegionLabel(subjectType, asset.guided_coverage_region) ?? "işaretlenmedi"}
            </span>
          )}
          <span className="faint">
            {MEDIA_TYPE_LABELS[asset.media_type]}
            {dimensions !== "—" && <> · {dimensions}</>} · {formatBytes(asset.size_bytes)}
            {asset.media_type === "video" && <> · {formatDuration(asset.duration_seconds)}</>}
            {" · "}
            {formatDateTime(asset.created_at)}
          </span>
        </div>

        {issues.length > 0 && (
          <ul className="media-viewer__issues">
            {issues.map((issue) => {
              const guidance = issueGuidance(issue.code);
              return (
                <li key={`${issue.code}-${issue.severity}`}>
                  <StatusBadge tone={SEVERITY_TONES[issue.severity] ?? "neutral"}>
                    {SEVERITY_LABELS[issue.severity] ?? "Ölçülen bulgu"}
                  </StatusBadge>{" "}
                  <span>{guidance.what}</span>{" "}
                  <span className="muted">{guidance.next}</span>
                </li>
              );
            })}
          </ul>
        )}

        {analysis.state === "assessed" && issues.length === 0 && verdict.key !== "good" && (
          // A grade with no issue rows behind it. Saying why would be an
          // invention; saying that there is no detail is the truth.
          <p className="muted">Bu dosya için ayrıntılı bir ölçüm kaydı yok.</p>
        )}

        <div className="media-viewer__nav">
          <Button disabled={index === 0} onClick={() => onIndex(index - 1)}>
            ← Önceki
          </Button>
          <span className="faint" aria-live="polite">
            {index + 1} / {count}
          </span>
          <Button busy={loadingMore} disabled={!canGoNext} onClick={goNext}>
            Sonraki →
          </Button>
        </div>

        <div className="media-viewer__footer">
          <DeleteMediaButton asset={asset} onDeleted={onChanged} />
          <Button variant="primary" onClick={onClose}>
            Kapat
          </Button>
        </div>
      </div>
    </div>
  );
}
